import { useContext, useEffect, useState } from 'react';
import type { Channel, Event as StreamEvent, StreamChat } from 'stream-chat';
import { ChatContext } from 'stream-chat-react';
import { useStreamChatContext } from '@features/contexts/ChatProvider';
import { StreamEventType } from '@features/components/Chat/constants';
import { getMembershipFilters } from '@features/components/Chat/components/hooks/useStreamChatFilters';

const bindChannelUnreadEvents = (channel: Channel, handler: () => void) => {
  channel.on(StreamEventType.MESSAGE_NEW, handler);
  channel.on(StreamEventType.MESSAGE_READ, handler);
  channel.on(StreamEventType.NOTIFICATION_MARK_READ, handler);
};

const unbindChannelUnreadEvents = (channel: Channel, handler: () => void) => {
  channel.off(StreamEventType.MESSAGE_NEW, handler);
  channel.off(StreamEventType.MESSAGE_READ, handler);
  channel.off(StreamEventType.NOTIFICATION_MARK_READ, handler);
};

const bindNewChannelListeners = (client: StreamChat, handler: (event: StreamEvent) => void) => {
  client.on(StreamEventType.NOTIFICATION_MESSAGE_NEW, handler);
  client.on(StreamEventType.ADDED_TO_CHANNEL, handler);
};

const unbindNewChannelListeners = (client: StreamChat, handler: (event: StreamEvent) => void) => {
  client.off(StreamEventType.NOTIFICATION_MESSAGE_NEW, handler);
  client.off(StreamEventType.ADDED_TO_CHANNEL, handler);
};

export const useCaseChatUnreadCount = (caseId: string) => {
  const chatContext = useContext(ChatContext);
  const streamChatContext = useStreamChatContext();
  const client = chatContext?.client;
  const isConnected = streamChatContext?.isConnected;
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!client || !client.userID || !isConnected || !caseId) {
      setUnreadCount(0);
      return undefined;
    }

    let mounted = true;
    let channels: Channel[] = [];

    const recalculate = () => {
      if (!mounted) return;
      setUnreadCount(channels.reduce((sum, channel) => sum + channel.countUnread(), 0));
    };

    const bindChannels = () => {
      channels.forEach((channel) => bindChannelUnreadEvents(channel, recalculate));
    };

    const unbindChannels = () => {
      channels.forEach((channel) => unbindChannelUnreadEvents(channel, recalculate));
    };

    const fetchChannels = async () => {
      try {
        const filters = getMembershipFilters(client.userID as string, caseId);
        const fetched = await client.queryChannels(filters);

        if (!mounted) return;

        unbindChannels();
        channels = fetched;
        bindChannels();
        recalculate();
      } catch {
        // failed to query channels
      }
    };

    const handlePossibleNewChannel = (event: StreamEvent) => {
      const eventCaseIds = (event.channel?.caseIds as string[] | undefined) ?? [];
      const isTracked = channels.some((channel) => channel.cid === event.cid);

      if (!isTracked && eventCaseIds.includes(caseId.toLowerCase())) {
        fetchChannels();
      }
    };

    fetchChannels();
    bindNewChannelListeners(client, handlePossibleNewChannel);

    return () => {
      mounted = false;
      unbindChannels();
      unbindNewChannelListeners(client, handlePossibleNewChannel);
    };
  }, [client, isConnected, caseId]);

  return unreadCount;
};
